import type { LineItemVariant } from '../line-items/line-items.types'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationIdInput,
  type ScopedAdminQueryKey,
} from '@/lib/organization-scoping'
import { formatPosUnitPrice, getPosVariantUnitPrice } from '@/lib/pos/inventory'
import {
  matchQuery,
  type MutationFilters,
  type MutationKey,
  queryOptions,
  useMutation,
  useQuery,
  useIsMutating,
  type UseQueryOptions,
} from '@tanstack/react-query'
import queryClient from '@/lib/queryClient'
import { normalizePlainText } from '@/lib/rich-text'
import { useSyncExternalStore } from 'react'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  createCart,
  addCartItem,
  addCustomCartItem,
  updateCartItem,
  removeCartItem,
  updateCart,
  deleteCart,
  getCart,
  getPosCart,
  getCartDiscountSuggestions,
  getRecoverablePosCartSummary,
  getSavedPosCarts,
  savePosCart,
  unsavePosCart,
  finalizeCartSalesOrder,
  refundCartPartialPayments,
  applyCartDiscount,
  applyCartTip,
} from './carts.api'
import { nextTempLineId, queueCartWrite, readCart, writingCart } from './cart-writer'
import { indexTableQueryOptions } from '../index-table-query-options'
import { salesOrderQueryKeys } from '../sales-orders/sales-orders.query'
import { cashDrawerQueryKeys } from '../cash-drawers/cash-drawers.query'
import { reportQueryKeys } from '../reports/reports.query'
import type {
  AddCustomCartItemVariables,
  ApplyCartDiscountVariables,
  ApplyCartTipVariables,
  Cart,
  CartDiscountData,
  CartDiscountSuggestionOptions,
  CartDiscountSuggestions,
  CartItemMutationResponse,
  CartQueryOptions,
  CreateCartInput,
  CustomCartItemInput,
  DeletedCart,
  FinalizeCartSalesOrderVariables,
  PosCart,
  PosCartItem,
  PosCartLineAdd,
  PosCartLineQuantity,
  RecoverablePosCartSummary,
  RecoverablePosCartSummaryOptions,
  RefundCartPartialPaymentsVariables,
  RemoveCartItemVariables,
  SavedPosCartList,
  SavedPosCartQueryOptions,
  SavePosCartInput,
  UpdateCartItemVariables,
  UpdateCartVariables,
} from './carts.types'

/** Without an organization, the root key matches every organization; hooks fall back to the route's. */
interface OptionalOrganizationScope {
  organizationId?: OrganizationIdInput
}

type CartQueryParams<TData> = OptionalOrganizationScope &
  Partial<Omit<UseQueryOptions<TData, Error, TData, ScopedAdminQueryKey>, 'queryKey' | 'queryFn'>>

/** Cart edits answer with the cart; payment, finalize, refund and POS item answers wrap it. */
type CartSyncResponse = { cart: Cart } | (Cart & { cart?: undefined })

interface SalesOrderSyncResponse {
  sales_order?: { id: number } | null
  cart?: { sales_order_id: number | null }
  salesOrderId?: number | null
}

/** Mutation variables carrying a cart id, or the bare cart id. */
type CartMutationTarget = number | { cartId: number }

export const cartQueryKeys = {
  all: ({ organizationId }: OptionalOrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'cart' }, { organizationId }),
  ],
  show: ({ organizationId, cartId }: CartQueryOptions) =>
    buildScopedAdminQueryKey(cartQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      cartId,
    }),
  posShow: ({ organizationId, cartId }: CartQueryOptions) =>
    buildScopedAdminQueryKey(cartQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'posShow',
      cartId,
    }),
  recoverablePosSummary: ({ organizationId, excludeCartId }: RecoverablePosCartSummaryOptions) =>
    buildScopedAdminQueryKey(cartQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'recoverablePosSummary',
      excludeCartId,
    }),
  savedPos: ({ organizationId, filters, page, pageSize }: SavedPosCartQueryOptions) =>
    buildScopedAdminQueryKey(cartQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'savedPos',
      filters,
      page,
      pageSize,
    }),
  discountSuggestions: ({ organizationId, cartId, search }: CartDiscountSuggestionOptions) =>
    buildScopedAdminQueryKey(cartQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'discountSuggestions',
      cartId,
      search: String(search ?? '').trim(),
    }),
}

export const cartQueries = {
  // Both reads keep the cart they have when a save overlapped them (cart-writer).
  show: ({ organizationId, cartId }: CartQueryOptions) =>
    queryOptions({
      queryKey: cartQueryKeys.show({ organizationId, cartId }),
      queryFn: (context) =>
        cartId
          ? readCart(organizationId, cartId, context.queryKey, () => getCart(context))
          : getCart(context),
      enabled: !!cartId,
    }),
  posShow: ({ organizationId, cartId }: CartQueryOptions) =>
    queryOptions<PosCart, Error, PosCart, ScopedAdminQueryKey>({
      queryKey: cartQueryKeys.posShow({ organizationId, cartId }),
      queryFn: (context) =>
        cartId
          ? readCart(organizationId, cartId, context.queryKey, () => getPosCart(context))
          : getPosCart(context),
      enabled: !!cartId,
    }),
  recoverablePosSummary: ({ organizationId, excludeCartId }: RecoverablePosCartSummaryOptions) =>
    queryOptions({
      queryKey: cartQueryKeys.recoverablePosSummary({ organizationId, excludeCartId }),
      queryFn: getRecoverablePosCartSummary,
      refetchInterval: 1000 * 60,
      gcTime: 0,
    }),
  savedPos: ({ organizationId, filters, page = 1, pageSize = 20 }: SavedPosCartQueryOptions) =>
    indexTableQueryOptions({
      queryKey: cartQueryKeys.savedPos({ organizationId, filters, page, pageSize }),
      queryFn: getSavedPosCarts,
      // Draft orders leave this list when someone completes them on another screen.
      staleTime: 0,
    }),
  discountSuggestions: ({ organizationId, cartId, search = '' }: CartDiscountSuggestionOptions) =>
    queryOptions({
      queryKey: cartQueryKeys.discountSuggestions({ organizationId, cartId, search }),
      queryFn: getCartDiscountSuggestions,
      enabled: !!cartId,
      staleTime: 1000 * 60,
    }),
}

export const cartMutationKeys = {
  create: ['cart', 'create'],
  addItem: ['cart', 'addItem'],
  addCustomItem: ['cart', 'addCustomItem'],
  updateItem: ['cart', 'updateItem'],
  removeItem: ['cart', 'removeItem'],
  update: ['cart', 'update'],
  applyDiscount: ['cart', 'applyDiscount'],
  applyTip: ['cart', 'applyTip'],
  delete: ['cart', 'delete'],
  savePos: ['cart', 'savePos'],
  unsavePos: ['cart', 'unsavePos'],
  prepareCardPayment: ['cart', 'prepareCardPayment'],
  createCashPaymentAndSalesOrder: ['cart', 'createCashPaymentAndSalesOrder'],
  createCheckPaymentAndSalesOrder: ['cart', 'createCheckPaymentAndSalesOrder'],
  finalizeSalesOrder: ['cart', 'finalizeSalesOrder'],
  refundPartialPayments: ['cart', 'refundPartialPayments'],
}

const DEFAULT_CUSTOM_ITEM_IMAGE_URL =
  import.meta.env.VITE_POS_CUSTOM_ITEM_DEFAULT_IMAGE_URL ||
  'https://cf.myonestem.com/media/default-custom-item-photo.svg'

const buildOptimisticCustomItemMedia = (customItem: CustomCartItemInput, fallbackName: string) => {
  const uploadedImageUrl =
    customItem?.image && typeof customItem.image === 'object'
      ? String(customItem.image.url ?? '').trim()
      : ''

  return [
    {
      name: fallbackName,
      url: uploadedImageUrl || DEFAULT_CUSTOM_ITEM_IMAGE_URL,
    },
  ]
}

const buildOptimisticCustomVariantDetails = (customItem: CustomCartItemInput): LineItemVariant => {
  const trimmedName = String(customItem?.name ?? '').trim() || 'Custom Item'
  const trimmedDescription = String(customItem?.description ?? '').trim()
  const trimmedNotes = String(customItem?.notes ?? '').trim()
  const recipe = normalizePlainText(String(customItem?.recipe ?? ''), { preserveSpaces: true })
  const formattedPrice = formatPosUnitPrice(customItem?.price)
  const fallbackImageName = trimmedName || 'Custom product photo'

  return {
    kind: 'custom',
    name: trimmedName,
    description: trimmedDescription || null,
    ...(trimmedNotes ? { notes: trimmedNotes } : {}),
    recipe: recipe || null,
    category_id: customItem?.category_id ?? null,
    category_name: customItem?.category_name ?? null,
    media: buildOptimisticCustomItemMedia(customItem, fallbackImageName),
    product: null,
    product_id: null,
    price: formattedPrice,
  }
}

const buildOptimisticCustomCartItem = (
  { cartId, quantity, customItem }: AddCustomCartItemVariables,
  lineId: number
): PosCartItem => {
  const formattedPrice = formatPosUnitPrice(customItem?.price)

  return {
    id: lineId,
    cart_id: cartId,
    variant_id: null,
    variant: null,
    quantity,
    price: formattedPrice,
    variant_details: buildOptimisticCustomVariantDetails(customItem),
  }
}

const normalizeCustomItemForPayload = (customItem: CustomCartItemInput): CustomCartItemInput => ({
  ...customItem,
  image: customItem?.image?.id ? { id: customItem.image.id } : null,
})

const getCartIdFromMutationInput = (input: CartMutationTarget) =>
  typeof input === 'object' && input !== null ? input.cartId : input

// Only PosCartResource serializes `source`; CartResource omits it.
const isPosCart = (cart: Cart): cart is PosCart => cart.source === 'pos'

const syncCartCacheFromResponse = (
  organizationId: number,
  response: CartSyncResponse,
  variables: CartMutationTarget
) => {
  const cart = response?.cart ? response.cart : response

  if (cart?.id) {
    queryClient.setQueryData<Cart>(cartQueryKeys.show({ organizationId, cartId: cart.id }), cart)
    if (isPosCart(cart)) {
      queryClient.setQueryData<PosCart>(
        cartQueryKeys.posShow({ organizationId, cartId: cart.id }),
        cart
      )
    }
  }

  return cart?.id ?? getCartIdFromMutationInput(variables)
}

const syncSalesOrderCacheFromResponse = (
  organizationId: number,
  response: SalesOrderSyncResponse
) => {
  const salesOrderId =
    response?.sales_order?.id ?? response?.cart?.sales_order_id ?? response?.salesOrderId ?? null

  if (salesOrderId) {
    void queryClient.invalidateQueries({
      queryKey: salesOrderQueryKeys.show({ organizationId, salesOrderId }),
    })
  }

  return salesOrderId
}

const invalidateCartIfPresent = (organizationId: number, cartId: number | undefined) => {
  if (!cartId) {
    return Promise.resolve()
  }

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: cartQueryKeys.show({ organizationId, cartId }) }),
    queryClient.invalidateQueries({ queryKey: cartQueryKeys.posShow({ organizationId, cartId }) }),
  ])
}

const cancelCartQuerySilently = (organizationId: number, cartId: number) =>
  Promise.all([
    queryClient.cancelQueries(
      { queryKey: cartQueryKeys.show({ organizationId, cartId }) },
      { silent: true }
    ),
    queryClient.cancelQueries(
      { queryKey: cartQueryKeys.posShow({ organizationId, cartId }) },
      { silent: true }
    ),
  ])

const refreshCartSnapshotAfterItemChange = async (organizationId: number, cartId: number) => {
  if (!cartId) {
    return
  }

  const queryKey = cartQueryKeys.posShow({ organizationId, cartId })

  try {
    const cart = await getPosCart({ queryKey })
    queryClient.setQueryData<PosCart>(queryKey, cart)
  } finally {
    await queryClient.invalidateQueries({ queryKey })
  }
}

const syncCartCacheFromItemResponse = async (
  organizationId: number,
  response: CartItemMutationResponse,
  variables: CartMutationTarget
) => {
  if (response?.cart) {
    syncCartCacheFromResponse(organizationId, response, variables)
    return
  }

  await refreshCartSnapshotAfterItemChange(organizationId, getCartIdFromMutationInput(variables))
}

export function useCreateCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationKey: cartMutationKeys.create,
    mutationFn: (data: CreateCartInput) => createCart({ organizationId, ...data }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: cartQueryKeys.all({ organizationId }) }),
  })
}

/** A line edit as the cart shows it before its save answers. */
function withLineEdit(
  cart: PosCart,
  {
    cartItemId,
    quantity,
    customItem,
    itemDetails,
    variant,
    variantId,
    subscriptionFrequency,
  }: UpdateCartItemVariables
): PosCart {
  return {
    ...cart,
    cart_items: cart.cart_items.map((item) =>
      item.id === cartItemId
        ? {
            ...item,
            // The line total is the server's (money rule 4); a quantity or price change waits for its answer.
            ...(quantity !== undefined || customItem || (variantId && variant)
              ? { subtotal: undefined }
              : {}),
            ...(quantity !== undefined ? { quantity } : {}),
            ...(customItem
              ? {
                  price: formatPosUnitPrice(customItem.price),
                  variant_details: buildOptimisticCustomVariantDetails(customItem),
                }
              : {}),
            ...(itemDetails
              ? {
                  variant_details: {
                    ...(item.variant_details ?? item.variant ?? {}),
                    ...Object.fromEntries(
                      Object.entries(itemDetails).map(([key, value]) => [
                        key,
                        String(value ?? '').trim() || null,
                      ])
                    ),
                  },
                }
              : {}),
            ...(subscriptionFrequency ? { subscription_frequency: subscriptionFrequency } : {}),
            ...(variantId
              ? {
                  variant_id: variantId,
                  ...(variant
                    ? {
                        price: getPosVariantUnitPrice(variant),
                        variant: {
                          ...variant,
                          product: variant.product ?? item.variant?.product ?? null,
                        },
                      }
                    : {}),
                }
              : {}),
          }
        : item
    ),
  }
}

/**
 * Saves a POS cart line change and writes the cart it answers with. A cart read already on its way
 * can't land after it. When the save fails, the cart is read again, so the screen ends on what the
 * server kept.
 */
async function savePosCartLine(
  organizationId: number,
  cartId: number,
  request: () => Promise<CartItemMutationResponse>
): Promise<CartItemMutationResponse> {
  await cancelCartQuerySilently(organizationId, cartId)

  let response: CartItemMutationResponse
  try {
    response = await writingCart(organizationId, cartId, request)
  } catch (error) {
    await refreshCartSnapshotAfterItemChange(organizationId, cartId).catch(() => undefined)
    throw error
  }
  await syncCartCacheFromItemResponse(organizationId, response, cartId)

  return response
}

/** A line change on the POS cart's line: it shows at once and saves after the cart's earlier writes. */
function queuePosCartLineWrite(
  organizationId: number,
  cartId: number,
  mutationKey: MutationKey,
  show: (cart: PosCart) => PosCart,
  request: () => Promise<CartItemMutationResponse>
): Promise<CartItemMutationResponse> {
  return queueCartWrite(organizationId, cartId, {
    mutationKey,
    show,
    send: () => savePosCartLine(organizationId, cartId, request),
  })
}

/**
 * Adds a catalog line to a POS cart now. The POS cart actions call it inside the add's queued
 * write, which shows the add meanwhile.
 */
export function addPosCartLine(
  organizationId: number,
  line: PosCartLineAdd
): Promise<CartItemMutationResponse> {
  return savePosCartLine(organizationId, line.cartId, () =>
    addCartItem({ organizationId, ...line })
  )
}

/**
 * Sets a POS cart line's quantity now. The POS cart actions call it inside the add's queued write,
 * which shows the add meanwhile.
 */
export function setPosCartLineQuantity(
  organizationId: number,
  line: PosCartLineQuantity
): Promise<CartItemMutationResponse> {
  return savePosCartLine(organizationId, line.cartId, () =>
    updateCartItem({ organizationId, ...line })
  )
}

/** Sets a POS cart line's quantity, after the cart's earlier writes. */
export function queuePosCartLineQuantity(
  organizationId: number,
  line: PosCartLineQuantity
): Promise<CartItemMutationResponse> {
  return queuePosCartLineWrite(
    organizationId,
    line.cartId,
    cartMutationKeys.updateItem,
    (cart) => withLineEdit(cart, line),
    () => updateCartItem({ organizationId, ...line })
  )
}

/** Removes a POS cart line, after the cart's earlier writes. */
export function queuePosCartLineRemoval(
  organizationId: number,
  line: RemoveCartItemVariables
): Promise<CartItemMutationResponse> {
  return queuePosCartLineWrite(
    organizationId,
    line.cartId,
    cartMutationKeys.removeItem,
    (cart) => ({
      ...cart,
      cart_items: cart.cart_items.filter((item) => item.id !== line.cartItemId),
    }),
    () => removeCartItem({ organizationId, ...line })
  )
}

/** Adds a custom line to a POS cart, after the cart's earlier writes. */
export function queuePosCartCustomLine(
  organizationId: number,
  line: AddCustomCartItemVariables
): Promise<CartItemMutationResponse> {
  const lineId = nextTempLineId()

  return queuePosCartLineWrite(
    organizationId,
    line.cartId,
    cartMutationKeys.addCustomItem,
    (cart) => ({
      ...cart,
      cart_items: [...cart.cart_items, buildOptimisticCustomCartItem(line, lineId)],
    }),
    () =>
      addCustomCartItem({
        organizationId,
        ...line,
        customItem: normalizeCustomItemForPayload(line.customItem),
      })
  )
}

/** Edits a POS cart line's custom item, details, option or subscription, after the cart's earlier writes. */
export function queuePosCartLineEdit(
  organizationId: number,
  edit: UpdateCartItemVariables
): Promise<CartItemMutationResponse> {
  return queuePosCartLineWrite(
    organizationId,
    edit.cartId,
    cartMutationKeys.updateItem,
    (cart) => withLineEdit(cart, edit),
    () =>
      updateCartItem({
        organizationId,
        ...edit,
        ...(edit.customItem ? { customItem: normalizeCustomItemForPayload(edit.customItem) } : {}),
      })
  )
}

/**
 * Saves a change to a cart on the cart's line, after its earlier writes, and writes the cart it
 * answers with before the next write starts. `refresh` reloads what the answer leaves stale, inside
 * the write, so the cart counts as saving until that's current too. The cart's edit hooks run their
 * saves through it; each hook's own mutation gives the form its pending and error state.
 */
function queueCartSave<TResponse extends CartSyncResponse>(
  organizationId: number,
  cartId: number,
  mutationKey: MutationKey,
  request: () => Promise<TResponse>,
  refresh?: (response: TResponse) => Promise<unknown>
): Promise<TResponse> {
  return queueCartWrite(organizationId, cartId, {
    mutationKey,
    send: async () => {
      const response = await writingCart(organizationId, cartId, request)
      syncCartCacheFromResponse(organizationId, response, cartId)
      await refresh?.(response)

      return response
    },
  })
}

export function useUpdateCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ cartId, data }: UpdateCartVariables) =>
      queueCartSave(organizationId, cartId, cartMutationKeys.update, () =>
        updateCart({ organizationId, cartId, data })
      ),
  })
}

export function getApplyCartDiscountMutationData({
  data,
  code,
  clear_discount_code: clearDiscountCode,
  type,
  value,
}: Omit<ApplyCartDiscountVariables, 'cartId'> = {}): CartDiscountData {
  if (data && typeof data === 'object') {
    return data
  }

  if (typeof code === 'string' && code.trim() !== '') {
    return { code }
  }

  if (clearDiscountCode) {
    return { clear_discount_code: true }
  }

  if (type === 'percent' || type === 'amount') {
    return { type, value }
  }

  return {}
}

export function useApplyCartDiscountMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ cartId, ...values }: ApplyCartDiscountVariables) =>
      queueCartSave(organizationId, cartId, cartMutationKeys.applyDiscount, () =>
        applyCartDiscount({
          organizationId,
          cartId,
          data: getApplyCartDiscountMutationData(values),
        })
      ),
  })
}

export function useApplyCartTipMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: ({ cartId, ...data }: ApplyCartTipVariables) =>
      queueCartSave(organizationId, cartId, cartMutationKeys.applyTip, () =>
        applyCartTip({ organizationId, cartId, data })
      ),
  })
}

export function useDeleteCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    // The cart's reads after the answer happen inside the write, so the cart counts as saving until
    // they're done.
    mutationFn: (cartId: number) =>
      queueCartWrite(organizationId, cartId, {
        mutationKey: cartMutationKeys.delete,
        send: async () => {
          let response: DeletedCart
          try {
            response = await writingCart(organizationId, cartId, () =>
              deleteCart({ organizationId, cartId })
            )
          } catch (error) {
            // A refusal means the cart holds a payment, maybe one taken elsewhere: show it as it is.
            await invalidateCartIfPresent(organizationId, cartId)
            throw error
          }
          // The page watches the cleared cart until it moves on, and a watched query that's removed is
          // read again at once, which the cleared cart answers with a 404. So its queries stay until
          // nothing watches them, when the query client drops them, and the refresh skips them.
          const clearedCart = {
            queryKey: [{ ...cartQueryKeys.all({ organizationId })[0], cartId }],
          }
          await queryClient.invalidateQueries({
            queryKey: cartQueryKeys.all({ organizationId }),
            predicate: (query) => !matchQuery(clearedCart, query),
          })

          return response
        },
      }),
  })
}

export function useSavePosCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: SavePosCartInput) =>
      queueCartSave(
        organizationId,
        values.cartId,
        cartMutationKeys.savePos,
        () => savePosCart({ organizationId, ...values }),
        () =>
          queryClient.invalidateQueries({ queryKey: cartQueryKeys.savedPos({ organizationId }) })
      ),
  })
}

export function useUnsavePosCartMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (cartId: number) =>
      queueCartSave(
        organizationId,
        cartId,
        cartMutationKeys.unsavePos,
        () => unsavePosCart({ organizationId, cartId }),
        () =>
          queryClient.invalidateQueries({ queryKey: cartQueryKeys.savedPos({ organizationId }) })
      ),
  })
}

export function useFinalizeCartSalesOrderMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    // The answer is the order page's cart, so the POS cart reads again before the write ends.
    mutationFn: (values: FinalizeCartSalesOrderVariables) =>
      queueCartSave(
        organizationId,
        values.cartId,
        cartMutationKeys.finalizeSalesOrder,
        () => finalizeCartSalesOrder({ organizationId, ...values }),
        (response) => {
          syncSalesOrderCacheFromResponse(organizationId, response)

          return Promise.all([
            invalidateCartIfPresent(organizationId, values.cartId),
            queryClient.invalidateQueries({
              queryKey: salesOrderQueryKeys.all({ organizationId }),
            }),
          ])
        }
      ),
  })
}

export function useRefundPartialPaymentsMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    // The answer is the order page's cart, so the POS cart reads again before the write ends.
    mutationFn: (values: RefundCartPartialPaymentsVariables) =>
      queueCartSave(
        organizationId,
        values.cartId,
        cartMutationKeys.refundPartialPayments,
        () => refundCartPartialPayments({ organizationId, ...values }),
        (response) => {
          syncSalesOrderCacheFromResponse(organizationId, {
            ...response,
            salesOrderId: values.salesOrderId,
          })

          return Promise.all([
            invalidateCartIfPresent(organizationId, values.cartId),
            queryClient.invalidateQueries({
              queryKey: salesOrderQueryKeys.all({ organizationId }),
            }),
            queryClient.invalidateQueries({
              queryKey: cashDrawerQueryKeys.all({ organizationId }),
            }),
            queryClient.invalidateQueries({
              queryKey: reportQueryKeys.cashDrawer({ organizationId }),
            }),
          ])
        }
      ),
  })
}

export function useCart(
  cartId: number | null | undefined,
  { organizationId, ...options }: CartQueryParams<Cart> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...cartQueries.show({ organizationId: resolvedOrganizationId, cartId }),
    ...options,
  })
}

export function usePosCart(
  cartId: number | null | undefined,
  { organizationId, ...options }: CartQueryParams<PosCart> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...cartQueries.posShow({ organizationId: resolvedOrganizationId, cartId }),
    ...options,
  })
}

export function useRecoverablePosCartSummary(
  excludeCartId: number | null | undefined,
  options: CartQueryParams<RecoverablePosCartSummary> & { orgId?: OrganizationIdInput } = {}
) {
  const { organizationId, orgId, ...restOptions } = options
  const resolvedOrganizationId = useRouteOrganizationId(organizationId ?? orgId)

  return useQuery({
    ...cartQueries.recoverablePosSummary({
      organizationId: resolvedOrganizationId,
      excludeCartId,
    }),
    ...restOptions,
  })
}

export function useSavedPosCarts({
  organizationId,
  filters,
  page,
  pageSize,
  ...options
}: Omit<SavedPosCartQueryOptions, 'organizationId'> & CartQueryParams<SavedPosCartList> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...cartQueries.savedPos({ organizationId: resolvedOrganizationId, filters, page, pageSize }),
    ...options,
  })
}

export function useCartDiscountSuggestions({
  cartId,
  search = '',
  organizationId,
  ...options
}: Omit<CartDiscountSuggestionOptions, 'organizationId'> &
  CartQueryParams<CartDiscountSuggestions>) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...cartQueries.discountSuggestions({
      organizationId: resolvedOrganizationId,
      cartId,
      search,
    }),
    ...options,
  })
}

// Cart changes on screen but not saved yet. These are module stores because the parts of the page
// that make the changes and the controls that must wait live in different parts of the page.
const unsavedCartChangeListeners = new Set<() => void>()

const subscribeToUnsavedCartChanges = (listener: () => void) => {
  unsavedCartChangeListeners.add(listener)
  return () => unsavedCartChangeListeners.delete(listener)
}

const notifyUnsavedCartChangeListeners = () => {
  unsavedCartChangeListeners.forEach((listener) => listener())
}

// Quantities typed into the POS cart, keyed by cart item. A typed value saves when its field
// blurs, which is usually the click on another cart control, so the cart counts as busy from the
// first keystroke until that save finishes.
const pendingQuantityEdits = new Set<number>()

const hasPendingQuantityEdit = () => pendingQuantityEdits.size > 0

export function setCartQuantityEditPending(cartItemId: number, isPending: boolean): void {
  if (pendingQuantityEdits.has(cartItemId) === isPending) {
    return
  }

  if (isPending) {
    pendingQuantityEdits.add(cartItemId)
  } else {
    pendingQuantityEdits.delete(cartItemId)
  }

  notifyUnsavedCartChangeListeners()
}

// Catalog adds the POS shows but hasn't sent yet, by POS cart actions queue (lib/pos/cart-actions).
// They count as saving: the totals show they're catching up, and other cart edits wait for them.
const queuedCartChangeOwners = new Set<object>()

const hasQueuedCartChanges = () => queuedCartChangeOwners.size > 0

export function setCartChangesQueued(owner: object, isQueued: boolean): void {
  if (queuedCartChangeOwners.has(owner) === isQueued) {
    return
  }

  if (isQueued) {
    queuedCartChangeOwners.add(owner)
  } else {
    queuedCartChangeOwners.delete(owner)
  }

  notifyUnsavedCartChangeListeners()
}

const cartMutationKeyValues: unknown[] = Object.values(cartMutationKeys).map((key) => key[1])

const isCartSaveMutation: NonNullable<MutationFilters['predicate']> = (mutation) => {
  const key = mutation.options.mutationKey
  return (
    Array.isArray(key) &&
    key.length >= 2 &&
    key[0] === 'cart' &&
    cartMutationKeyValues.includes(key[1])
  )
}

// Cart saves in flight or queued, for showing save progress. A write waiting its turn on the cart's
// line is a pending mutation, so it counts.
export function useIsCartSaving(): number {
  const savingCount = useIsMutating({ predicate: isCartSaveMutation })
  const hasQueuedChanges = useSyncExternalStore(subscribeToUnsavedCartChanges, hasQueuedCartChanges)

  return savingCount + (hasQueuedChanges ? 1 : 0)
}

const orderEndingMutationKeyValues: unknown[] = [
  cartMutationKeys.delete,
  cartMutationKeys.prepareCardPayment,
  cartMutationKeys.createCashPaymentAndSalesOrder,
  cartMutationKeys.createCheckPaymentAndSalesOrder,
  cartMutationKeys.finalizeSalesOrder,
  cartMutationKeys.refundPartialPayments,
].map((key) => key[1])

const isOrderEndingMutation: NonNullable<MutationFilters['predicate']> = (mutation) =>
  isCartSaveMutation(mutation) &&
  orderEndingMutationKeyValues.includes(mutation.options.mutationKey?.[1])

/** True while the order is being paid for, finalized, or cleared. */
export function isCartOrderEnding(): boolean {
  return queryClient.isMutating({ predicate: isOrderEndingMutation }) > 0
}

/** Whether the order is being paid for, finalized, or cleared, for controls that wait meanwhile. */
export function useIsCartOrderEnding(): boolean {
  return useIsMutating({ predicate: isOrderEndingMutation }) > 0
}

/**
 * Resolves once no cart save is in flight, so a queued change starts from the saved cart. Queued
 * changes don't count: their queue is what waits here.
 */
export function waitForCartSaves(): Promise<void> {
  const isIdle = () => queryClient.isMutating({ predicate: isCartSaveMutation }) === 0

  if (isIdle()) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    const unsubscribe = queryClient.getMutationCache().subscribe(() => {
      if (isIdle()) {
        unsubscribe()
        resolve()
      }
    })
  })
}

// Gives you an isPending flag for any and all cart mutations, including a typed quantity that
// is about to save; anything that changes the cart should wait while this is above zero.
export function useIsCartMutating(): number {
  const savingCount = useIsCartSaving()
  const hasQuantityEdit = useSyncExternalStore(
    subscribeToUnsavedCartChanges,
    hasPendingQuantityEdit
  )

  return savingCount + (hasQuantityEdit ? 1 : 0)
}
